import { jsPDF } from "jspdf";

// ─── constants ───────────────────────────────────────────────────────────────
const ID_MONTHS = [
  "Januari","Februari","Maret","April","Mei","Juni",
  "Juli","Agustus","September","Oktober","November","Desember",
];

const JENIS_LABEL = {
  PPH01: "Self Declare",
  PPH02: "UMK Reguler",
  PPH03: "Reguler",
};

// Page 2 materi table rows
const MATERI_ROWS = [
  { type: "section", no: "I",    materi: "Pengetahuan Dasar Penyelia Halal",                                        jp: "" },
  { type: "item",    no: "1.1",  materi: "Regulasi dan Ruang Lingkup Jaminan Produk Halal",                         jp: 1 },
  { type: "item",    no: "1.2",  materi: "Konsep Halal Haram dan Fatwa Kehalalan Produk",                           jp: 1 },
  { type: "item",    no: "1.3",  materi: "Pengantar Sistem Jaminan Produk Halal (SJPH)",                            jp: 2 },
  { type: "section", no: "II",   materi: "Keterampilan Kerja dan Kompetensi Penyelia",                              jp: "" },
  { type: "item",    no: "2.1",  materi: "Menyusun dokumen penerapan SJPH dan kelengkapannya",                      jp: 1 },
  { type: "item",    no: "2.2",  materi: "Menyiapkan dokumen daftar bahan dan dokumen pendukung",                   jp: 2 },
  { type: "item",    no: "2.3",  materi: "Mengawasi Bahan, Proses dan Produk Halal",                                jp: 1 },
  { type: "item",    no: "2.4",  materi: "Melakukan penanganan produk yang tidak memenuhi kriteria halal",          jp: 0.5 },
  { type: "item",    no: "2.5",  materi: "Melakukan audit internal penerapan SJPH",                                 jp: 1 },
  { type: "item",    no: "2.6",  materi: "Melakukan evaluasi tindak lanjut hasil audit internal",                   jp: 0.5 },
  { type: "section", no: "III",  materi: "Aplikasi Sistem Informasi",                                               jp: "" },
  { type: "item",    no: "3.1",  materi: "Melakukan pendaftaran sertifikat halal melelui SIHALAL",                  jp: 2 },
];
const TOTAL_JP = 12;

// ─── helpers ──────────────────────────────────────────────────────────────────
const formatDateId = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, "0")} ${ID_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const formatDateRange = (tglMulai, tglSelesai) => {
  if (!tglMulai) return "...";
  const d1 = new Date(tglMulai);
  const d2 = tglSelesai ? new Date(tglSelesai) : d1;
  if (
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  ) {
    return `${d1.getDate()} \u2013 ${d2.getDate()} ${ID_MONTHS[d1.getMonth()]} ${d1.getFullYear()}`;
  }
  return `${d1.getDate()} ${ID_MONTHS[d1.getMonth()]} \u2013 ${d2.getDate()} ${ID_MONTHS[d2.getMonth()]} ${d1.getFullYear()}`;
};

/**
 * Load image, resize to maxW×maxH (preserving aspect ratio), return as
 * compressed data-URL. format='jpeg' for opaque images, 'png' for transparency.
 */
const toResized = (url, maxW, maxH = maxW, format = "jpeg", quality = 0.75) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.round(img.width  * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width  = w;
        c.height = h;
        const ctx = c.getContext("2d");
        if (format === "jpeg") {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, w, h);
        }
        ctx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL(`image/${format}`, quality));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

/**
 * Like toResized but removes near-white background pixels first (for RGB PNGs
 * that have a white background instead of transparency).
 */
const toResizedTransparent = (url, maxW, maxH = maxW) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        // Step 1: remove white bg at original size
        const c1 = document.createElement("canvas");
        c1.width  = img.width;
        c1.height = img.height;
        const ctx1 = c1.getContext("2d");
        ctx1.drawImage(img, 0, 0);
        const id = ctx1.getImageData(0, 0, c1.width, c1.height);
        const d  = id.data;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i] > 220 && d[i + 1] > 220 && d[i + 2] > 220) d[i + 3] = 0;
        }
        ctx1.putImageData(id, 0, 0);

        // Step 2: resize
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const w = Math.round(img.width  * scale);
        const h = Math.round(img.height * scale);
        const c2 = document.createElement("canvas");
        c2.width  = w;
        c2.height = h;
        c2.getContext("2d").drawImage(c1, 0, 0, w, h);
        resolve(c2.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

// ─── main export ─────────────────────────────────────────────────────────────
/**
 * Generate a 2-page landscape A4 sertifikat PDF.
 * Returns { blob, filename } so the caller can upload and/or download.
 *
 * @param {object} data            – row from sertifikat-data endpoint
 * @param {string} baseUrl         – backend base URL
 * @param {string} nomorSertifikat – full cert number entered by admin
 * @param {string} tglTerbit      – ISO date string for the cert issue date
 */
export const generateSertifikat = async (data, baseUrl, nomorSertifikat, tglTerbit) => {
  const {
    no_register, nama, tempat_lahir, tgl_lahir, nik,
    alamat_lengkap, kecamatan, kabupaten, provinsi,
    nama_instansi, file_foto, jenis_pelatihan, angkatan, jadwal,
  } = data;

  // ── Load static & dynamic images in parallel ────────────────────────────
  const origin = window.location.origin;
  const imgPath = (name) => `${origin}/images/sertifikat/${name}`;

  const [
    imgBg, imgBpjph, imgHalalCenter,
    imgTtdKepala, imgTtdKetua, imgStempel,
    imgTtdMot, imgFoto,
  ] = await Promise.all([
    toResized(imgPath("bg-wave.png"),           1200, 850, "jpeg", 0.65),  // background, opaque
    toResizedTransparent(imgPath("logo-bpjph.png"),    160, 160),           // logo, white→transparent
    toResized(imgPath("logo-halal-center.png"), 160, 160, "png"),           // logo, keep alpha
    toResized(imgPath("ttd-kepala.png"),        300, 200, "png"),           // signature
    toResized(imgPath("ttd-ketua.png"),         300, 200, "png"),           // signature
    toResized(imgPath("stempel.png"),           200, 200, "png"),           // stamp, keep alpha
    toResized(imgPath("ttd-mot.png"),           300, 200, "png"),           // signature
    file_foto ? toResized(`${baseUrl}/uploads/penyelia/${file_foto}`, 200, 280, "jpeg", 0.80) : Promise.resolve(null),
  ]);

  // ── Derived strings ─────────────────────────────────────────────────────
  const certNo   = nomorSertifikat;
  const certDate = formatDateId(tglTerbit);

  const alamat = [
    alamat_lengkap,
    kecamatan ? `Kec. ${kecamatan}` : "",
    kabupaten ? `${kabupaten}` : "",
    provinsi  ? `Prov. ${provinsi}` : "",
  ].filter(Boolean).join(", ");

  const tempatTglLahir = `${tempat_lahir || ""}, ${formatDateId(tgl_lahir)}`;
  const jenisLabel     = JENIS_LABEL[jenis_pelatihan] || jenis_pelatihan;
  const trainingDates  = jadwal ? formatDateRange(jadwal.tgl_mulai, jadwal.tgl_selesai) : "...";

  // ── Create PDF ──────────────────────────────────────────────────────────
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W  = 297;
  const H  = 210;
  const cx = W / 2;

  // ════════════════════ PAGE 1 ════════════════════
  if (imgBg) doc.addImage(imgBg, "JPEG", 0, 0, W, H);

  // ── Header logos ─────────────────────────────────────────────────────────
  const logoTop = 8;
  const logoH   = 22;
  const logoSz  = 20; // shared size for both logos

  if (imgBpjph) {
    doc.addImage(imgBpjph, "PNG", cx - 58, logoTop + 1, logoSz, logoSz);
  }
  // BPJPH text  — purple, slightly larger
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(90, 30, 120);
  doc.text("BADAN PENYELENGGARA",  cx - 36, logoTop + 7);
  doc.text("JAMINAN PRODUK HALAL", cx - 36, logoTop + 13);

  if (imgHalalCenter) {
    doc.addImage(imgHalalCenter, "PNG", cx + 10, logoTop + 1, logoSz, logoSz);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(20, 80, 20);
  doc.text("HALAL CENTER", cx + 30, logoTop + 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text("UIN Sunan Kalijaga Yogyakarta", cx + 30, logoTop + 16);

  // ── Position reference (below logo area) ─────────────────────────────────
  const hrY = logoTop + logoH + 2;

  // ── Title ────────────────────────────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(20, 20, 20);
  doc.text("SERTIFIKAT PELATIHAN PENYELIA HALAL", cx, hrY + 9, { align: "center" });

  // ── Cert number ──────────────────────────────────────────────────────────
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.text(`NOMOR: ${certNo}`, cx, hrY + 16, { align: "center" });

  // ── Diberikan Kepada ─────────────────────────────────────────────────────
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.text("Diberikan Kepada", cx, hrY + 23, { align: "center" });

  // ── Photo ────────────────────────────────────────────────────────────────
  const contentY = hrY + 27;
  const photoX = 15;
  const photoW = 32;
  const photoH = 44;

  doc.setDrawColor(100, 100, 100);
  doc.setLineWidth(0.3);
  doc.rect(photoX, contentY, photoW, photoH);
  if (imgFoto) {
    doc.addImage(imgFoto, "JPEG", photoX, contentY, photoW, photoH);
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text("Foto", photoX + photoW / 2, contentY + photoH / 2, { align: "center" });
  }

  // ── Data fields ──────────────────────────────────────────────────────────
  const dataX    = photoX + photoW + 7;
  const colonX   = dataX + 58;
  const valueX   = colonX + 4;
  const maxValW  = W - valueX - 15;
  let   fieldY   = contentY + 6;
  const lineH    = 7;

  const drawField = (label, value, bold = false) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text(label, dataX, fieldY);
    doc.text(":", colonX, fieldY);

    if (bold) doc.setFont("helvetica", "bold");
    const lines = doc.splitTextToSize(value || "-", maxValW);
    doc.text(lines, valueX, fieldY);
    if (bold) doc.setFont("helvetica", "normal");

    fieldY += lines.length > 1 ? lineH * lines.length * 0.72 : lineH;
  };

  drawField("Nama", nama, true);
  drawField("Tempat dan Tanggal Lahir", tempatTglLahir);
  drawField("NIK", nik);
  drawField("Alamat", alamat);
  drawField("Institusi/Lembaga", nama_instansi);

  // ── Paragraph ────────────────────────────────────────────────────────────
  const paraY   = contentY + photoH + 6;
  const paraStr = `Telah LULUS pada Pelatihan Penyelia Halal Angkatan ${angkatan} \u2013 ${jenisLabel} yang diselenggarakan Halal Center UIN Sunan Kalijaga Yogyakarta dengan Sistem Daring tanggal ${trainingDates}.`;
  const paraLines = doc.splitTextToSize(paraStr, W - dataX - 15);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10.5);
  doc.setTextColor(30, 30, 30);
  doc.text(paraLines, dataX, paraY);

  // ── Signature area ───────────────────────────────────────────────────────
  const sigY  = paraY + paraLines.length * 5.8 + 6;
  const lSigX = 20;
  const rSigX = W - 90;

  // Left header
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(30, 30, 30);
  doc.text("Mengetahui", lSigX, sigY);
  doc.text("Badan Penyelenggara Jaminan Produk Halal", lSigX, sigY + 5);

  // Right header  
  doc.text(`Yogyakarta, ${certDate}`, rSigX, sigY);
  doc.text("Halal Center UIN Sunan Kalijaga", rSigX, sigY + 5);

  // Stamp + signatures
  const imgY    = sigY + 7;
  const imgH    = 22;
  const nameY   = imgY + imgH + 3;

  if (imgTtdKepala) doc.addImage(imgTtdKepala, "PNG", lSigX, imgY, 40, imgH);
  if (imgStempel)   doc.addImage(imgStempel,   "PNG", rSigX,      imgY, 22, 22);
  if (imgTtdKetua)  doc.addImage(imgTtdKetua,  "PNG", rSigX + 12, imgY, 34, imgH);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Dr. H. Ahmad Haikal Hassan, ST., MT.", lSigX, nameY);
  doc.setFont("helvetica", "normal");
  doc.text("Kepala", lSigX, nameY + 4.5);

  doc.setFont("helvetica", "bold");
  doc.text("Dr. Imelda Fajriati, M.Si.", rSigX, nameY);
  doc.setFont("helvetica", "normal");
  doc.text("Ketua", rSigX, nameY + 4.5);

  // ════════════════════ PAGE 2 ════════════════════
  doc.addPage();
  if (imgBg) doc.addImage(imgBg, "JPEG", 0, 0, W, H);

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(20, 20, 20);
  doc.text("MATERI PELATIHAN", cx, 22, { align: "center" });

  // ── Table ────────────────────────────────────────────────────────────────
  const tblX    = 20;
  const tblW    = W - 40;
  const col1W   = 14;    // No
  const col3W   = 28;    // JP
  const col2W   = tblW - col1W - col3W;
  const col2X   = tblX + col1W;
  const col3X   = tblX + col1W + col2W;
  const rH      = 6.5;   // row height

  let tY = 28;

  // Header row
  doc.setFillColor(200, 200, 200);
  doc.rect(tblX, tY, tblW, rH * 1.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(20, 20, 20);
  doc.text("No",            tblX + col1W / 2,    tY + 7, { align: "center" });
  doc.text("Materi",        col2X + col2W / 2,   tY + 7, { align: "center" });
  doc.text("Jam Pelajaran", col3X + col3W / 2,   tY + 4, { align: "center" });
  doc.text("(JP)",          col3X + col3W / 2,   tY + 8, { align: "center" });

  // Header border
  const drawBorders = (y, h) => {
    doc.setDrawColor(130, 130, 130);
    doc.setLineWidth(0.25);
    doc.line(tblX,         y,     tblX,         y + h);
    doc.line(col2X,        y,     col2X,        y + h);
    doc.line(col3X,        y,     col3X,        y + h);
    doc.line(tblX + tblW,  y,     tblX + tblW,  y + h);
    doc.line(tblX,         y + h, tblX + tblW,  y + h);
  };

  drawBorders(tY, rH * 1.5);
  tY += rH * 1.5;

  // Data rows
  MATERI_ROWS.forEach((row) => {
    const isSection = row.type === "section";
    if (isSection) {
      doc.setFillColor(230, 230, 230);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    doc.rect(tblX, tY, tblW, rH, "F");

    doc.setFont("helvetica", isSection ? "bold" : "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(20, 20, 20);

    doc.text(String(row.no),  tblX + col1W / 2, tY + 4.3, { align: "center" });
    doc.text(row.materi,      col2X + 2,         tY + 4.3);
    if (row.jp !== "") {
      doc.text(String(row.jp), col3X + col3W / 2, tY + 4.3, { align: "center" });
    }

    drawBorders(tY, rH);
    tY += rH;
  });

  // Total row
  doc.setFillColor(200, 200, 200);
  doc.rect(tblX, tY, tblW, rH, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("Jumlah", col3X - 3, tY + 4.3, { align: "right" });
  doc.text(String(TOTAL_JP), col3X + col3W / 2, tY + 4.3, { align: "center" });
  drawBorders(tY, rH);
  // Top outer line
  doc.setDrawColor(80, 80, 80);
  doc.setLineWidth(0.5);
  doc.line(tblX, 28, tblX + tblW, 28);
  doc.line(tblX, tY + rH, tblX + tblW, tY + rH);
  tY += rH;

  // Keterangan JP
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(40, 40, 40);
  doc.text("*Keterangan: 1 JP = 60 menit", tblX, tY + 5);
  tY += 13;

  // MOT signature
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Yogyakarta, ${certDate}`, tblX, tY);
  tY += 5;
  doc.text("Master of Training", tblX, tY);
  tY += 3;

  if (imgTtdMot) {
    doc.addImage(imgTtdMot, "PNG", tblX, tY, 40, 20);
    tY += 22;
  } else {
    tY += 22;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text("Dr. Rer.medic. Esti Wahyu Widowati, M.Si., M.Biotech.", tblX, tY);

  // Footer bar
  const fY = H - 10;
  doc.setFillColor(60, 60, 60);
  doc.rect(0, fY - 3, W, 13, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(255, 255, 255);
  doc.text(
    "Halal Center UIN Sunan Kalijaga  Jl. Laksda Adisucipto, Yogyakarta, Indonesia 55281.  " +
    "Email : halalcenter@uin-suka.ac.id.  Website : halalcenter.uin-suka.ac.id",
    cx, fY + 3, { align: "center" }
  );

  // ── Return blob + filename ────────────────────────────────────────────────
  const safeName = (nama || "peserta").replace(/\s+/g, "_");
  const filename = `Sertifikat_${safeName}_${no_register}.pdf`;
  const blob = doc.output("blob");
  return { blob, filename };
};
